import { useMemo } from 'react';
import { useCurrentUser } from '~/hooks/queries/use-is-admin';
import {
  useAgentManagement,
  useEnabledAgentIds,
  useMyDisabledAgents,
} from '~/hooks/queries/use-agent-management';
import { useCustomAgents, useDisabledCustomAgentIds } from '~/hooks/queries/use-workspace-custom-agents';
import { useAvailableProviders } from '~/hooks/queries/use-providers';
import {
  buildCustomRows,
  buildPredefinedRows,
  connectableCapabilityIds,
  sortAgentRows,
} from './agents-overview-model';

/**
 * The Agents page's rows — predefined and custom, with the same visibility and
 * classification rules — as ONE hook, so the command palette (FRONT-2436) finds
 * exactly the agents the Agents page lists rather than its own re-derivation.
 *
 * `enabled` is for the palette, which is always mounted and must read nothing
 * until someone searches. The page leaves it on. `withConnectability: false`
 * skips the providers read for a caller that shows no connect state (the
 * palette): rows then carry `connectable` null and never wait on it.
 */
export function useAgentOverviewRows(
  me: ReturnType<typeof useCurrentUser>,
  canAuthorAgents: boolean,
  options?: { enabled?: boolean; withConnectability?: boolean },
) {
  const enabled = options?.enabled ?? true;
  const withConnectability = options?.withConnectability ?? true;
  const {
    data: predefinedAgents,
    isLoading: predefinedAgentsLoading,
    error: predefinedError,
  } = useAgentManagement({ enabled });
  const { data: enabledAgentIds, isLoading: enabledLoading } = useEnabledAgentIds({ enabled });
  const { data: myDisabled, isLoading: myDisabledLoading } = useMyDisabledAgents({ enabled });
  // FRONT-602: what the Connections page offers, to tell "not connected yet"
  // from "nobody here can connect it". Its in-flight flag joins the
  // predefined pending set below, so a locked row never first paints as a
  // live switch someone could click. A FAILED read is not pending: the list
  // renders with `connectable` null and no row greyed — it never breaks over
  // this one read.
  const { data: providers, isLoading: providersLoading } = useAvailableProviders({
    enabled: enabled && withConnectability,
  });
  const connectable = useMemo(() => (providers ? connectableCapabilityIds(providers) : null), [providers]);

  const {
    data: customAgents,
    isLoading: customAgentsLoading,
    error: customError,
  } = useCustomAgents({ enabled });
  const { data: disabledCustomIds, isLoading: disabledCustomLoading } = useDisabledCustomAgentIds({ enabled });

  // CLAUDE.md FRONT-1525: loading is keyed off the queries' own in-flight
  // flags, never `!data` — and an error gets its own branch below, never
  // folded into "still loading".
  const predefinedPending = predefinedAgentsLoading || enabledLoading || myDisabledLoading || providersLoading;
  const predefinedLoaded = !predefinedPending && !predefinedError;

  // `me.isLoading` is in the custom pending set: classification into
  // Mine/Shared needs the viewer's own id, so rendering before it resolves
  // would misclassify every custom agent as Shared for one paint.
  const customPending =
    customAgentsLoading || disabledCustomLoading || myDisabledLoading || me.isLoading;
  const customLoaded = !customPending && !customError;

  const predefinedRows = useMemo(
    () =>
      predefinedLoaded
        ? buildPredefinedRows(predefinedAgents ?? [], enabledAgentIds ?? null, myDisabled ?? [], connectable)
        : [],
    [predefinedLoaded, predefinedAgents, enabledAgentIds, myDisabled, connectable],
  );

  const customRows = useMemo(
    () =>
      customLoaded
        ? buildCustomRows(
            customAgents ?? [],
            disabledCustomIds ?? [],
            myDisabled ?? [],
            me.userId,
            me.isAdmin,
            canAuthorAgents,
          )
        : [],
    [customLoaded, customAgents, disabledCustomIds, myDisabled, me.userId, me.isAdmin, canAuthorAgents],
  );

  const allRows = useMemo(
    () => sortAgentRows([...predefinedRows, ...customRows]),
    [predefinedRows, customRows],
  );

  return {
    predefinedPending,
    predefinedError,
    predefinedLoaded,
    customPending,
    customError,
    customLoaded,
    customRows,
    allRows,
  };
}
